/**
 * Format numbers with K, M, B suffixes
 * @param num - The number to format
 * @returns Formatted string with suffix
 */
export function formatNumWithKMB(num: number): string {
    if (num === null || num === undefined) return '';
    
    const absNum = Math.abs(num);
    
    if (absNum >= 1e9) {
        const formatted = (num / 1e9).toFixed(2);
        // Remove trailing zeros and decimal point if not needed
        return formatted.replace(/\.?0+$/, '') + 'B';
    }
    if (absNum >= 1e6) {
        const formatted = (num / 1e6).toFixed(2);
        // Remove trailing zeros and decimal point if not needed
        return formatted.replace(/\.?0+$/, '') + 'M';
    }
    if (absNum >= 1e3) {
        const formatted = (num / 1e3).toFixed(2);
        // Remove trailing zeros and decimal point if not needed
        return formatted.replace(/\.?0+$/, '') + 'K';
    }
    
    // For numbers less than 1000, show as integer if whole number, otherwise 2 decimal places
    if (Number.isInteger(num)) {
        return num.toString();
    }
    return num.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Like formatNumWithKMB but never strips fractional zeros: always two digits after the decimal
 * when a fractional part is shown (e.g. 7.9 → "7.90"). Whole numbers under 1000 stay unpadded (e.g. 12).
 * K/M/B scaled values always use two decimal places (e.g. "5.00K").
 */
export function formatNumWithKMBTwoDecimalPlaces(num: number): string {
    if (num === null || num === undefined) return '';

    const absNum = Math.abs(num);

    if (absNum >= 1e9) {
        return `${(num / 1e9).toFixed(2)}B`;
    }
    if (absNum >= 1e6) {
        return `${(num / 1e6).toFixed(2)}M`;
    }
    if (absNum >= 1e3) {
        return `${(num / 1e3).toFixed(2)}K`;
    }

    if (Number.isInteger(num)) {
        return num.toString();
    }
    return num.toFixed(2);
}

/**
 * Truncate text to a specified length
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis
 */
export function truncate(text: string, maxLength: number = 15): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
}

/** Font family for all chart text (labels, axis points, data values) - hard rule */
export const CHART_FONT_FAMILY = 'Open Sans';

/** Font family with fallback for canvas/CSS (quoted for names with spaces) */
export const CHART_FONT_FAMILY_CSS = "'Open Sans', sans-serif";

/** Max character length for x-axis labels before ellipsis (used across bar, line, BPL charts) */
export const X_AXIS_LABEL_MAX_LENGTH = 30;

/** Max characters per line when wrapping axis labels */
export const AXIS_LABEL_WRAP_CHARS = 24;

/** Max lines for wrapped axis labels before ellipsis */
export const AXIS_LABEL_MAX_LINES = 2;

/**
 * Format x-axis label: wrap if needed, truncate with ellipsis if longer than maxLength
 * Handles both string and non-string values (e.g. numeric x-axis like quarter numbers).
 * @param text - The label text (string or number/other - will be coerced to string)
 * @param maxLength - Max chars before ellipsis (default 24)
 * @returns Formatted label
 */
export function formatXAxisLabel(text: string | number | unknown, maxLength: number = X_AXIS_LABEL_MAX_LENGTH): string {
    if (text === null || text === undefined) return '';
    const str = String(text);
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
}

/**
 * Format axis label with wrapping: wrap into multiple lines at maxCharsPerLine (default 24),
 * then add ellipsis if still exceeds maxLines. Uses newline for wrapping (G2/SVG supports \n).
 * @param text - The label text
 * @param maxCharsPerLine - Max characters per line (default 24)
 * @param maxLines - Max lines before ellipsis (default 2)
 * @returns Formatted label with newlines for wrapping
 */
export function formatAxisLabelWithWrap(
    text: string,
    maxCharsPerLine: number = AXIS_LABEL_WRAP_CHARS,
    maxLines: number = AXIS_LABEL_MAX_LINES,
): string {
    if (!text) return '';
    if (text.length <= maxCharsPerLine) return text;

    const lines: string[] = [];
    let remaining = text;

    for (let i = 0; i < maxLines && remaining.length > 0; i++) {
        if (remaining.length <= maxCharsPerLine) {
            lines.push(remaining);
            remaining = '';
        } else {
            // Find a good break point (space) if possible, otherwise hard break
            const chunk = remaining.substring(0, maxCharsPerLine);
            const lastSpace = chunk.lastIndexOf(' ');
            const breakAt = lastSpace > maxCharsPerLine * 0.5 ? lastSpace : maxCharsPerLine;
            lines.push(remaining.substring(0, breakAt));
            remaining = remaining.substring(breakAt).trimStart();
        }
    }

    let result = lines.join('\n');
    if (remaining.length > 0) {
        result = result + '...';
    }
    return result;
}
